import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { Row, Col, Image, ListGroup, Card, Button, Form, Container, Collapse } from 'react-bootstrap';
import { useDispatch } from 'react-redux';
import Rating from '../components/Rating';
import { FaCheck, FaTimes } from 'react-icons/fa';
import Loader from '../components/Loader.jsx';
import Message from '../components/Message.jsx';
import Meta from '../components/Meta.jsx';
import { useGetProductDetailsQuery, useGetProductReviewsByIdQuery } from '../slices/productsApiSlice.js';
import { addToCart } from '../slices/cartSlice.js';
import { dateFormatterShort } from '../utils/dateHelper.js';

const ProductScreen = () => {
  const { id: productId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [quantity, setQuantity] = useState(1);
  const [selectedPriceCustomization, setPriceCustomization] = useState(0);
  const [selectedSpiceLevel, setSpiceLevel] =  useState('Regular');
  const [selectedSweetness, setSweetness] = useState('Regular');
  const [showDescription, setShowDescription] = useState(false);

  const { data: product, isLoading, error } = useGetProductDetailsQuery(productId);
  
  const {
    data: reviews,
    isLoading: isLoadingReviews,
    error: isErrorReviews,
  } = useGetProductReviewsByIdQuery(productId);
    
  const addToCartHandler = () => {
    dispatch(addToCart({
      ...product,
      quantity,
      selectedPriceCustomization,
      selectedSpiceLevel,
      selectedSweetness
    }));
    navigate('/cart');
  };

  return (
    <>
      {
        isLoading ? (
          <Loader/>
        ) : error ? (
          <Message variant="danger">
            { error?.data?.message || error.error }
          </Message>
        ) : (
          <Container>
            <Meta
              title={`${product.name} | Flavchap Telugu Snacks & Sweets`}
              description={product.description}
              keywords={`${product.name}, snacks, sweets, homemade, home made, telugu, telangana, andhra, pindi vantalu, telangana pindi vantalu, andhra pindi vantalu, home foods, swagruha foods, swagruha, food ecommerce, portal, website, hyderabad, home delivery, deliver to home, food delivery, home made foods, home made snacks, home made sweets, home made snacks and sweets`}
              image={product.image}
              url={`https://www.flavchap.com/product/${product._id}`}
              jsonLd={[
                {
                  "@context": "https://schema.org",
                  "@type": "Product",
                  name: product.name,
                  image: product.image,
                  description: product.description,
                  sku: product._id,
                  brand: { "@type": "Brand", name: "Flavchap" },
                  offers: {
                    "@type": "Offer",
                    url: `https://www.flavchap.com/product/${product._id}`,
                    priceCurrency: "INR",
                    price: product.customization.priceCustomization[0].price,
                    availability: "https://schema.org/InStock"
                  }
                },
                {
                  "@context": "https://schema.org",
                  "@type": "BreadcrumbList",
                  itemListElement: [
                    {
                      "@type": "ListItem",
                      position: 1,
                      name: "Home",
                      item: "https://www.flavchap.com/"
                    },
                    {
                      "@type": "ListItem",
                      position: 2,
                      name: product.name,
                      item: `https://www.flavchap.com/product/${product._id}`
                    }
                  ]
                }
              ]}
            />
            <Link to='/' className='btn btn-light mt-1 mb-3'>Back</Link>
            <Row>
              <Col md={8}>
                <Row>
                  <Col xs={12} sm={4} md={4} className="mb-3 mb-md-0 text-center">
                    <div className='ratio ratio-1x1'>
                      <Image
                        src={product.image}
                        alt={product.name}
                        className="w-100 h-100"
                        style={{objectFit: 'cover'}}
                      />
                    </div>
                  </Col>
                  {/* Right-side details */}
                  <Col xs={12} sm={8} md={8}>
                    <ListGroup variant="flush">
                      <ListGroup.Item className='py-1'>
                        <h3 className='mb-1'>{product.name}</h3>
                        <div className='mb-1 white-section'>
                        {isLoadingReviews ? (
                          <Loader />
                        ) : isErrorReviews ? (
                          <Message variant="danger">
                            {isErrorReviews?.data?.message || isErrorReviews.error}
                          </Message>
                        ) : (
                          <Rating
                            value={
                              reviews && reviews.length > 0
                                ? Math.round(
                                    (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length) * 10
                                  ) / 10
                                : 0
                            }
                            text={
                              reviews && reviews.length > 0
                              ? `(${Math.round(
                                (reviews.reduce((acc, r) => acc + r.rating, 0) / reviews.length) * 10
                                ) / 10}) - ${reviews.length === 1
                                  ? reviews.length + ' review' 
                                  : reviews.length  + ' reviews'}`
                              : 'No reviews yet'
                            }
                          />
                        )}
                        </div>
                      </ListGroup.Item>
                      {/* Customizations */}
                      <ListGroup.Item className='pt-1 pb-2'>
                        <Row>
                          <Col xs={12} sm={6} md={6} className="mb-2">
                            <Form.Label>Weight</Form.Label>
                            <div className="d-flex flex-wrap gap-2">
                              {product.customization.priceCustomization.map((option, index) => (
                                <Button
                                  key={index}
                                  size="sm"
                                  variant={selectedPriceCustomization === index ? "primary" : "outline-primary"}
                                  onClick={() => setPriceCustomization(index)}
                                >
                                  {option.weight}
                                </Button>
                              ))}
                            </div>
                          </Col>

                          {product.subCategory === "Hot Snacks" && (
                            <Col xs={12} sm={6} md={6}>
                              <Form.Label>Spice Level</Form.Label>
                              <div className="d-flex flex-wrap gap-2">
                                {product.customization.spiceLevel.map((option) => (
                                  <Button
                                    key={option}
                                    size="sm"
                                    variant={selectedSpiceLevel === option ? "primary" : "outline-primary"}
                                    onClick={() => setSpiceLevel(option)}
                                  >
                                    {option}
                                  </Button>
                                ))}
                              </div>
                            </Col>
                          )}

                          {product.subCategory === "Sweets" && (
                            <Col xs={12} sm={6} md={6}>
                              <Form.Label>Sweetness</Form.Label>
                              <div className="d-flex flex-wrap gap-2">
                                {product.customization.sweetness.map((option) => (
                                  <Button
                                    key={option}
                                    size="sm"
                                    variant={selectedSweetness === option ? "primary" : "outline-primary"}
                                    onClick={() => setSweetness(option)}
                                  >
                                    {option}
                                  </Button>
                                ))}
                              </div>
                            </Col>
                          )}
                        </Row>
                      </ListGroup.Item>
                      <ListGroup.Item className='py-1'>
                        <p>Price: <strong>₹{product.customization.priceCustomization[selectedPriceCustomization].price}</strong></p>
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                </Row>
                <Row className='my-3'>
                  <Col>
                    <ListGroup>
                      <ListGroup.Item className="py-2">
                        {/* Header row */}
                        <div
                          className="d-flex justify-content-between align-items-center"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setShowDescription(!showDescription)}
                          aria-controls="product-description"
                          aria-expanded={showDescription}
                        >
                          <strong>Description</strong>
                          <span
                            style={{
                              transition: 'transform 0.2s ease',
                              transform: showDescription ? 'rotate(180deg)' : 'rotate(0deg)',
                            }}
                          >
                            ▼
                          </span>
                        </div>

                        {/* Collapsible content */}
                        <Collapse in={showDescription}>
                          <div id="product-description" className="mt-2">
                            <p className="mb-0">
                              {product.description}
                              {product.alternateNames && (
                                <>
                                  <br />
                                  <br />
                                  <strong>Also called:</strong> {product.alternateNames}
                                </>
                              )}
                            </p>
                          </div>
                        </Collapse>
                      </ListGroup.Item>
                    </ListGroup>
                  </Col>
                </Row>
              </Col>
              <Col md={4}>
                <Card className='h-auto'>
                  <ListGroup>
                    <ListGroup.Item>
                      <Row>
                        <Col>Price: </Col>
                        <Col>
                          <strong>₹{product.customization.priceCustomization[selectedPriceCustomization].price}</strong>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    <ListGroup.Item>
                      <Row>
                        <Col>Available: </Col>
                        <Col>
                          <strong>{product.available === true ? <FaCheck color="green"/> : <FaTimes color="red"/>}</strong>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    {product.available === true && (
                    <ListGroup.Item>
                      <Row>
                        <Col>Quantity</Col>
                        <Col>
                          <Form.Control
                            as="select"
                            size="sm"
                            value={quantity}
                            onChange={(e) => setQuantity(Number(e.target.value))}>
                            {[...Array(10).keys()].map((x) => (
                              <option key={x + 1} value={x + 1}>
                                {x + 1}
                              </option>
                            ))}
                          </Form.Control>
                        </Col>
                      </Row>
                    </ListGroup.Item>
                    )}
                    <ListGroup.Item>
                      <Button
                        className='w-100' type='button'
                        disabled={product.available === false}
                        onClick={addToCartHandler}>
                        Add To Cart
                      </Button>
                    </ListGroup.Item>
                  </ListGroup>
                </Card>
              </Col>
            </Row>
            <Row className="review">
              <Col md={8}>
                <h2>Reviews</h2>
                {isLoadingReviews ? (
                  <Loader />
                ) : isErrorReviews ? (
                  <Message variant="danger">
                    {isErrorReviews?.data?.message || isErrorReviews.error}
                  </Message>
                ) : reviews?.length === 0 ? (
                  <Message>No product reviews yet. Would be delighted to get a review of this product from you soon.</Message>
                ) : (
                  <ListGroup variant="flush" className="white-section">
                    {reviews.map((review) => (
                      <ListGroup.Item key={review._id}>
                        <strong>{review.name}</strong>
                        <Rating
                          value={review.rating} 
                          text={`reviewed on ${dateFormatterShort(review.createdAt)} by ${review.user?.name}`}
                        />
                        <p>{review.comment}</p>
                      </ListGroup.Item>
                    ))}
                  </ListGroup>
                )}
              </Col>
            </Row>
          </Container>
        )
      }
    </>
  )
}

export default ProductScreen;